import { useEffect, useState, useCallback } from "react";
import api, { apiErrorMessage } from "../lib/api";
import { Plus, Users, Trash2, Shield, Pencil } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { EmptyState } from "./OpenShifts";
import { toast } from "sonner";

const TITLES = ["CT Technologist", "Lead CT Technologist", "Radiologist", "Nurse", "Front Desk", "CT Chief Technologist"];
const EMP = ["Full-time", "Part-time", "Per Diem"];

const blank = { email: "", password: "", name: "", role: "staff", title: "CT Technologist", employment_type: "Full-time", phone: "" };

export default function StaffManagement({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(blank);

  const load = useCallback(async () => {
    try { setUsers((await api.get("/users")).data); }
    catch (err) { toast.error(apiErrorMessage(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditId(null); setForm(blank); setOpen(true); };
  const openEdit = (u) => { setEditId(u.id); setForm({ ...blank, ...u, password: "" }); setOpen(true); };

  const submit = async () => {
    if (!form.name || !form.email) return toast.error("Name and email required");
    if (!editId && !form.password) return toast.error("Password required for new staff");
    try {
      if (editId) {
        const payload = { name: form.name, role: form.role, title: form.title, employment_type: form.employment_type, phone: form.phone };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editId}`, payload);
        toast.success("Staff updated");
      } else {
        await api.post("/users", form);
        toast.success("Staff account created");
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(apiErrorMessage(err)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(apiErrorMessage(err)); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-display">Staff</h1>
          <p className="text-sm text-slate-500 mt-1">Create and manage accounts for your department.</p>
        </div>
        <Button onClick={openNew} data-testid="add-staff-button" className="gap-2 bg-slate-900 hover:bg-slate-800"><Plus className="h-4 w-4" />Add Staff</Button>
      </div>

      {users.length === 0 ? (
        <EmptyState icon={Users} text="No staff accounts yet." />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {users.map((u) => (
            <div key={u.id} data-testid={`staff-card-${u.id}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-full bg-gradient-to-br from-sky-500 to-indigo-600 text-white flex items-center justify-center text-sm font-bold shrink-0">
                  {u.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="font-semibold text-slate-900 truncate">{u.name}</span>
                    {u.role === "admin" && <Shield className="h-3.5 w-3.5 text-sky-600" />}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{u.email}</div>
                  <div className="text-xs text-slate-400 mt-1">{u.title} · {u.employment_type}</div>
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openEdit(u)} data-testid={`edit-staff-${u.id}`} className="gap-1.5 flex-1"><Pencil className="h-3.5 w-3.5" />Edit</Button>
                {u.id !== currentUser.id && (
                  <Button size="sm" variant="outline" onClick={() => remove(u.id)} data-testid={`delete-staff-${u.id}`} className="text-rose-600"><Trash2 className="h-4 w-4" /></Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="staff-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit staff member" : "Add staff member"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Full name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name-input" /></div>
              <div className="space-y-2"><Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="staff-phone-input" /></div>
            </div>
            <div className="space-y-2"><Label>Email</Label>
              <Input type="email" value={form.email} disabled={!!editId} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email-input" /></div>
            <div className="space-y-2"><Label>{editId ? "New password (leave blank to keep)" : "Password"}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-password-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Title</Label>
                <Select value={form.title} onValueChange={(v) => setForm({ ...form, title: v })}>
                  <SelectTrigger data-testid="staff-title-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{TITLES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-2"><Label>Employment</Label>
                <Select value={form.employment_type} onValueChange={(v) => setForm({ ...form, employment_type: v })}>
                  <SelectTrigger data-testid="staff-employment-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{EMP.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="space-y-2"><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="admin">Super User (Admin)</SelectItem>
                </SelectContent>
              </Select></div>
          </div>
          <DialogFooter><Button onClick={submit} data-testid="submit-staff-button" className="bg-slate-900 hover:bg-slate-800">{editId ? "Save Changes" : "Create Account"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
